import { useDeepCompareEffect } from "@react-hookz/web";
import { MapLayerMouseEvent } from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useCallback, useMemo, useState } from "react";
import Map, { NavigationControl, ViewState } from "react-map-gl";
import { Spinner, Typography } from "@/components/atoms";
import { useBookingTripMap } from "../hooks/useBookingTripMap";
import { BookingTripMarkers } from "./BookingTripMarkers";
import { BookingTripOverridePopup } from "./BookingTripOverridePopup";
import { BookingTripPoints } from "./BookingTripPoints";
import { BookingTripPolylines } from "./BookingTripPolylines";

const ACCESS_TOKEN = import.meta.env.VITE_RIDEMINDER_MAPBOX_ACCESS_TOKEN;
const MAP_STYLE = "mapbox://styles/rideminder/clq7mqg8s001d01ppczpf3bqn";

interface BookingTripViewerProps {
  showOverrideForm?: boolean;
  isLoading?: boolean;
}

export const BookingTripViewer = ({ showOverrideForm = true, isLoading = false }: BookingTripViewerProps) => {
  const {
    mode,
    tripLog,
    map: { ref: mapRef, tripPoints },
    functions: { setMapBounds, setActiveTripPoint },
  } = useBookingTripMap();

  const recenterMap = useCallback(() => {
    switch (tripLog.job.status) {
      case "on_way_to_job":
        setMapBounds("driver-on-way");
        break;

      case "commenced":
        setMapBounds("commenced");
        break;

      case "awaiting_driver_allocation":
      case "driver_allocated_confirmed":
      case "driver_allocated_not_confirmed":
      default:
        setMapBounds("all");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useDeepCompareEffect(() => {
    recenterMap();
  }, [tripLog]);

  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [viewState] = useState<ViewState | undefined>(undefined);
  const layerIds = useMemo(() => tripPoints.map((_, idx) => `location-point-${idx}-fill`), [tripPoints]);

  const handleMapClick = useCallback(
    (event: MapLayerMouseEvent) => {
      const features = event.features;

      if (features && features.length > 0 && tripPoints.length > 0) {
        const feature = features[0];
        const layerIndex = feature.layer.id.split("-")[2];
        const tripPoint = tripPoints[Number(layerIndex)];

        if (layerIndex) setActiveTripPoint(tripPoint);
      }
    },
    [setActiveTripPoint, tripPoints]
  );

  const handleMapLoad = useCallback(() => {
    setIsMapLoaded(true);
    recenterMap();
  }, [recenterMap]);

  return (
    <div className="relative h-full w-full">
      {(!isMapLoaded || isLoading) && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-neutral-surface-gray">
          <Spinner className="mr-0 h-[30px] w-[30px] mb-2.5" />
          <Typography variant="action" className="text-neutral-dark-gray">
            Loading trip map...
          </Typography>
          <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
            This may take a few seconds for longer trips.
          </Typography>
        </div>
      )}
    <Map
      mapStyle={MAP_STYLE}
      mapboxAccessToken={ACCESS_TOKEN}
      onLoad={handleMapLoad}
      ref={mapRef}
      onClick={handleMapClick}
      interactiveLayerIds={layerIds}
      initialViewState={{
        latitude: -32.60410130519325,
        longitude: 146.2514176872145,
        zoom: 5,
      }}
      {...viewState}
      cooperativeGestures={mode === "guest"}
    >
      <NavigationControl />
      <BookingTripMarkers />
      <BookingTripPolylines />
      {mode !== "guest" && (
        <>
          <BookingTripPoints />
          <BookingTripOverridePopup showOverrideForm={showOverrideForm} />
        </>
      )}
    </Map>
    </div>
  );
};
